import { createAsyncThunk } from '@reduxjs/toolkit'
import RestClient from '../../../../services/client/RestClient'
import { addToArrIfNotExistOrRemove, isNotEmpty } from '../../../../utils/helpers'
import CustomerAdminUsers from '../../../../services/api/CustomerAdminUser'

const getCustomerAdminUsersApi = CustomerAdminUsers.Customer_Admin_Users_Endpoints

const resetState = {
  pageNumber: 0,
  activeFilters: [],
  activeSearch: '',
  updatedItem: {},
}

export const customerAdminResetAlertHandler = createAsyncThunk(
  'customerAdminUsers/customerAdminResetAlertHandler',
  async () => ({}),
)

export const retrieveCustomerAdminUsers = createAsyncThunk(
  'customerAdminUsers/retrieveCustomerAdminUsers',
  async ({ queries, resetQueries }, { rejectWithValue, getState }) => {
    const apiUri = getCustomerAdminUsersApi;
    const { customerAdminUsers } = getState()
    const endpoint = apiUri.list;
    const queryString = createReqHelper(customerAdminUsers, resetQueries ? resetState : queries)
    const response = await RestClient.getRequest(`${endpoint}?${queryString}`);

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return {
      response: response.data,
      queries: resetQueries ? resetState : queries,
    };
  },
)

export const filterCustomerAdminUsers = createAsyncThunk(
  'customerAdminUsers/filterCustomerAdminUsers',
  async ({ queries }, { getState, rejectWithValue }) => {
    const { customerAdminUsers } = getState()
    const apiUri = getCustomerAdminUsersApi
    let newFilterArr = normalizeCustomerAdminActiveFilters(customerAdminUsers?.activeFilters)

    if (queries === 'clear') {
      newFilterArr = []
    } else if (queries?.userTypes) {
      newFilterArr = Array.isArray(queries.userTypes) ? [...queries.userTypes] : []
    } else if (Array.isArray(queries)) {
      newFilterArr = [...queries]
    } else if (
      (customerAdminUsers?.filterList || []).some((e) => e.name === queries?.name)
    ) {
      newFilterArr = addToArrIfNotExistOrRemove(newFilterArr, 'id', queries)
    }

    const filters = createReqHelper(customerAdminUsers, {
      activeFilters: newFilterArr,
      pageNumber: 0,
    })

    const endpoint = apiUri.list;
    const response = await RestClient.getRequest(`${endpoint}?${filters}`);

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return {
      response: response.data,
      queries: {
        activeFilters: newFilterArr,
        pageNumber: 0,
      },
    }
  },
)

export const retrieveCustomerAdminUserByID = createAsyncThunk(
  'customerAdminUsers/getCustomerAdminUserById',
  async ({ queries }, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.getUserId;
    const response = await RestClient.getRequest(`${endpoint}${queries.id}`);

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const customerAdminUserChangeStatus = createAsyncThunk(
  'sysAdminUsers/changeStatus',
  async ({ queries }, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.list;
    const response = await RestClient.putRequest(`${endpoint}/${queries.id}`, {
      active: queries.status,
    });

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const customerAdminEditUser = createAsyncThunk(
  'customerAdmin/editUser',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }

    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title
    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }

    const endpoint = getCustomerAdminUsersApi.list;
    const response = await RestClient.putRequest(`${endpoint}/${queries.updatedUser?.id}`, newQuery);

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been updated successfully'],
    }
  },
)

export const customerAdminCreateUsers = createAsyncThunk(
  'customerAdmin/createUsers',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }
    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title

    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }
    const endpoint = getCustomerAdminUsersApi.list;

    const response = await RestClient.postRequest(`${endpoint}`, newQuery)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been added successfully'],
    }
  },
)

export const getCountForAllNonRegisterUsers = createAsyncThunk(
  'customerAdminUsers/getCountForAllNonRegisterUsers',
  async (_, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.getCountForAllNonRegisterUsers;
    const response = await RestClient
      .getRequest(`${process.env.REACT_APP_CORE_FRONTEND_URL}/otms-rest/api/${endpoint}`)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const postBulkCreateForAllUsers = createAsyncThunk(
  'customerAdminUsers/postBulkCreateForAllUsers',
  async ({ queries }, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.bulkCreateUsers;
    const response = await RestClient.postRequest(`${endpoint}`, queries)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const getBulkCreateUsersIsValid = createAsyncThunk(
  'customerAdminUsers/getBulkCreateUsersIsValid',
  async (_, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.bulkCreateUsersValidate;
    const response = await RestClient.getRequest(`${endpoint}`)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const getBulkCreateUsersProcessIsValid = createAsyncThunk(
  'customerAdminUsers/getBulkCreateUsersProcessIsValid',
  async (_, { rejectWithValue }) => {
    const endpoint = getCustomerAdminUsersApi.bulkCreateUsersProcessValidate;
    const response = await RestClient.getRequest(`${endpoint}`)

    if (response.status >= 300) {
      return rejectWithValue(response)
    }

    return response.data
  },
)

export const customerAdminUpdateSelectedUser = createAsyncThunk(
  'customerAdminUsers/customerAdminUpdateSelectedUser',
  async ({ queries }) => queries,
)
function normalizeCustomerAdminActiveFilters(activeFilters) {
  if (!activeFilters) return []
  if (Array.isArray(activeFilters)) return [...activeFilters]
  return [...(activeFilters.userTypes || [])]
}

function createReqHelper(store, filters = {}) {
  const copyStore = { ...store, ...filters };

  const raw = copyStore.activeFilters
  const userTypesList = Array.isArray(raw) ? raw : (raw?.userTypes || [])
  const getFilterIds = (filtersId) => (filtersId || []).map((filter) => filter.id)

  const filterUserTypeIds = getFilterIds(userTypesList).join('&user_types=')

  const {
    pageSize,
    pageNumber,
    activeSort,
    activeSearch,
    activeSummary,
  } = copyStore;

  return [
    `page_number=${pageNumber || 0}`,
    `page_size=${pageSize || 9}`,
    `sort_by=${activeSort?.key || ''}`,
    `data_status=${activeSummary || ''}`,
    `user_types=${filterUserTypeIds}`,
    `search=${activeSearch || ''}`,
  ].join('&');
}
